import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import styles from './NovelVoting.module.css';

// 简化的 Icon 组件
const StarIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const LoaderIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <circle cx="12" cy="12" r="10" strokeWidth="4" strokeDasharray="50 50" opacity="0.25"/>
    <circle cx="12" cy="12" r="10" strokeWidth="4" strokeDasharray="50 50" strokeLinecap="round">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
    </circle>
  </svg>
);

const CheckIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ArrowLeftIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
);

const TrophyIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22M18 2H6v7a6 6 0 0 0 12 0V2z"/>
  </svg>
);

const HeartIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const PlusIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const SearchIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.35-4.35"/>
  </svg>
);

const NovelVoting = () => {
  const navigate = useNavigate();
  const [novels, setNovels] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [votingNovelId, setVotingNovelId] = useState(null);
  
  // 添加小说建议相关状态
  const [showAddModal, setShowAddModal] = useState(false);
  const [novelName, setNovelName] = useState('');
  const [novelAuthor, setNovelAuthor] = useState('');
  const [addingNovel, setAddingNovel] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [novelsResponse, statsResponse] = await Promise.all([
        api.get('/novel-votes/novels'),
        api.get('/novel-votes/stats')
      ]);
      
      setNovels(novelsResponse.data);
      setUserStats(statsResponse.data);
      setError(null);
    } catch (err) {
      console.error('获取投票数据失败:', err);
      setError('加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleVote = async (votableNovelId) => {
    if (votingNovelId) return;
    
    setVotingNovelId(votableNovelId);
    
    try {
      await api.post('/novel-votes/', { novel_id: votableNovelId });
      await fetchData();
      alert('投票成功！');
    } catch (err) {
      console.error('投票失败:', err);
      if (err.response?.status === 409) {
        alert('您已经为该小说投过票了');
      } else {
        alert(err.response?.data?.detail || '投票失败，请稍后重试');
      }
    } finally {
      setVotingNovelId(null);
    }
  };

  const handleSubmitSuggestion = async () => {
    if (!novelName.trim()) {
      alert('请输入小说名称');
      return;
    }

    setAddingNovel(true);
    try {
      await api.post('/novel-votes/suggest-novel', { 
        novel_name: novelName.trim(),
        author: novelAuthor.trim() || null
      });
      alert('建议提交成功！感谢您的推荐，管理员会尽快处理。');
      setShowAddModal(false);
      setNovelName('');
      setNovelAuthor('');
    } catch (err) {
      console.error('提交失败:', err);
      alert(err.response?.data?.detail || '提交失败，请稍后重试');
    } finally {
      setAddingNovel(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <LoaderIcon size={48} />
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h3>加载失败</h3>
          <p>{error}</p>
          <button onClick={fetchData}>重试</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate('/profile')}
          >
            <ArrowLeftIcon size={20} />
          </button>
          <h1 className={styles.title}>
            <TrophyIcon size={24} />
            投票
          </h1>
        </div>

        <div className={styles.headerRight}>
          {userStats && (
            <div className={styles.votesBadge}>
              <HeartIcon size={16} />
              <span>{userStats.total_votes_count} 票</span>
            </div>
          )}
          <button
            type="button"
            className={styles.addButtonCompact}
            onClick={() => setShowAddModal(true)}
          >
            <PlusIcon size={18} />
            <span>添加</span>
          </button>
        </div>
      </div>

      <div className={styles.novelsList}>
        {novels.map((novel) => (
          <div 
            key={novel.votable_novel_id} 
            className={`${styles.novelCard} ${novel.user_has_voted ? styles.votedCard : ''}`}
          >
            <div className={styles.rankBadge}>
              {novel.rank <= 3 ? (
                <span className={styles[`rank${novel.rank}`]}>
                  #{novel.rank}
                </span>
              ) : (
                <span className={styles.rankNormal}>#{novel.rank}</span>
              )}
            </div>


            <div className={styles.novelInfo}>
              <h3 className={styles.novelTitle}>{novel.novel_name}</h3>
              {novel.author && (
                <p className={styles.novelAuthor}>作者：{novel.author}</p>
              )}
              
              <div className={styles.voteInfo}>
                <StarIcon size={16} />
                <span>{novel.total_votes} 票</span>
              </div>
            </div>

            <div className={styles.voteActions}>
              {novel.user_has_voted ? (
                <div className={styles.votedBadge}>
                  <CheckIcon size={16} />
                  已投票
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.voteButton}
                  onClick={() => handleVote(novel.votable_novel_id)}
                  disabled={votingNovelId === novel.votable_novel_id}
                >
                  {votingNovelId === novel.votable_novel_id ? (
                    <>
                      <LoaderIcon size={16} />
                      投票中...
                    </>
                  ) : (
                    <>
                      <HeartIcon size={16} />
                      投票
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {novels.length === 0 && (
        <div className={styles.emptyState}>
          <TrophyIcon size={48} />
          <p>暂无小说建议，点击上方按钮添加</p>
        </div>
      )}

      {/* 添加小说弹窗 */}
      {showAddModal && (
        <div className={styles.modal} onClick={() => setShowAddModal(false)}>
          <div className={styles.modalContentSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>建议添加小说</h2>
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            <p className={styles.inputHint}>
                建议您希望添加的小说，管理员会根据投票情况考虑添加
              </p>
            <div className={styles.inputGroup}>
              <label htmlFor="novelNameInput" className={styles.inputLabel}>
                小说名称 <span className={styles.required}>*</span>
              </label>
              <input
                id="novelNameInput"
                type="text"
                placeholder="请输入小说名称"
                value={novelName}
                onChange={(e) => setNovelName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmitSuggestion()}
                className={styles.novelIdInput}
                autoFocus
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="authorInput" className={styles.inputLabel}>
                作者 <span className={styles.optional}>（选填）</span>
              </label>
              <input
                id="authorInput"
                type="text"
                placeholder="请输入作者名称"
                value={novelAuthor}
                onChange={(e) => setNovelAuthor(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmitSuggestion()}
                className={styles.novelIdInput}
              />
             
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setShowAddModal(false)}
              >
                取消
              </button>
              <button
                type="button"
                className={styles.confirmButton}
                onClick={handleSubmitSuggestion}
                disabled={!novelName.trim() || addingNovel}
              >
                {addingNovel ? (
                  <>
                    <LoaderIcon size={16} />
                    提交中...
                  </>
                ) : (
                  <>
                    {/* <PlusIcon size={16} /> */}
                    提交建议
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NovelVoting;

